import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import client from "@/lib/api";
import { SectionHeader, EmptyState, MLFields, notifyError, notifyOk } from "./_ui";
import { REF_FIELD_NAMES, REF_FIELDS, clearDecoderRefsCache } from "@/lib/decoderRefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const emptyItem = (field) => ({
  field, code: "", label: { de: "", en: "", pl: "", ru: "" },
  keywords: [], order: 0, active: true,
});

export default function DecoderRefs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyItem("fuel_type"));
  const [editing, setEditing] = useState(null);

  const load = () =>
    client.get("/admin/decoder-refs").then((r) => setItems(r.data)).catch(notifyError);
  useEffect(() => { load(); }, []);

  const openNew = (field) => { setForm(emptyItem(field)); setEditing(null); setOpen(true); };
  const openEdit = (it) => {
    setForm({ ...emptyItem(it.field), ...it, label: { de: "", en: "", pl: "", ru: "", ...(it.label || {}) } });
    setEditing(it.id); setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim()) { notifyError({ message: "Укажите код значения" }); return; }
    const payload = {
      ...form,
      code: form.code.trim(),
      keywords: (Array.isArray(form.keywords) ? form.keywords : String(form.keywords).split(","))
        .map((k) => k.trim()).filter(Boolean),
    };
    try {
      if (editing) await client.put(`/admin/decoder-refs/${editing}`, payload);
      else await client.post("/admin/decoder-refs", payload);
      setOpen(false); notifyOk("Сохранено"); clearDecoderRefsCache(); load();
    } catch (e) { notifyError(e); }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить значение?")) return;
    await client.delete(`/admin/decoder-refs/${id}`);
    clearDecoderRefsCache(); load();
  };

  return (
    <div>
      <SectionHeader
        title="Справочники декодера"
        description="Значения справочных полей (топливо, двигатель, привод, кузов) с переводами. Используются в «Базе VIN» и при импорте из partsapi."
      />

      <div className="space-y-8">
        {REF_FIELDS.map((field) => {
          const list = items.filter((i) => i.field === field).sort((a, b) => (a.order || 0) - (b.order || 0));
          return (
            <div key={field} data-testid={`refcat-${field}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold">{REF_FIELD_NAMES[field]}</h2>
                <Button data-testid={`ref-add-${field}`} size="sm" variant="outline" onClick={() => openNew(field)}>
                  <Plus className="w-4 h-4 mr-1.5" /> Значение
                </Button>
              </div>
              {list.length === 0 ? (
                <EmptyState text="Значений нет." />
              ) : (
                <div className="border border-neutral-200 rounded-lg bg-white divide-y divide-neutral-100">
                  {list.map((it) => (
                    <div key={it.id} data-testid={`ref-row-${it.id}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <code className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600">{it.code}</code>
                      <span className="flex-1">{it.label?.ru} <span className="text-neutral-400">· {it.label?.de} / {it.label?.en} / {it.label?.pl}</span></span>
                      {!it.active && <span className="text-xs text-neutral-400">выкл</span>}
                      <button onClick={() => openEdit(it)} className="p-1.5 text-neutral-600 hover:text-black" data-testid={`ref-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(it.id)} className="p-1.5 text-red-600" data-testid={`ref-delete-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Редактировать значение" : "Новое значение"} · {REF_FIELD_NAMES[form.field]}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Код (латиница, без пробелов)</Label>
              <Input data-testid="ref-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="border-neutral-300 font-mono" placeholder="например, petrol" />
            </div>
            <MLFields value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="ref-label" label="Название (4 языка)" />
            <div className="space-y-1.5">
              <Label>Ключевые слова для распознавания partsapi (через запятую)</Label>
              <Input data-testid="ref-keywords" value={Array.isArray(form.keywords) ? form.keywords.join(", ") : form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="border-neutral-300" placeholder="бензин, petrol, benzin" />
            </div>
            <div className="flex items-center gap-6">
              <div className="space-y-1.5">
                <Label>Порядок</Label>
                <Input data-testid="ref-order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="border-neutral-300 w-24" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch data-testid="ref-active" checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <Label>Активно</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Отмена</Button>
            <Button data-testid="ref-save-btn" onClick={save} style={{ background: "var(--ci-accent)", color: "#1a1a1a" }}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
